import React from "react";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";

export const SERVICE_CATEGORY_SLUGS = [
    "event-management",
    "groceries",
    "fruits-vegetables",
    "home-maid",
    "mutual-fund-distributor",
    "insurance-distributor",
];

export const isServiceCategory = (slug) => SERVICE_CATEGORY_SLUGS.includes(slug);

const EVENT_TYPES = [
    "Wedding",
    "Birthday Party",
    "Corporate Event",
    "Conference / Seminar",
    "Engagement / Reception",
    "Anniversary",
    "Religious / Pooja",
    "Concert / Live Show",
    "Festival Celebration",
    "Private Party",
];
const PRICE_UNITS_EVENT = ["Per Event", "Per Day", "Per Hour", "Per Person"];
const PAY_MODES = ["Cash", "UPI", "Card", "Bank Transfer", "Wallet (Paytm/PhonePe)"];
const PRODUCE_UNITS = ["Per Kg", "Per Dozen", "Per Piece", "Per Bunch", "Per Crate"];
const HOMEMAID_TYPES = [
    "Cooking",
    "Cleaning",
    "Childcare / Babysitting",
    "Elderly Care",
    "Cooking + Cleaning",
    "Full-time (Live-in)",
    "Part-time (Hourly)",
    "Driver",
    "Pet Care",
];
const HOMEMAID_RATE_UNITS = ["Per Hour", "Per Day", "Per Month"];
const GENDER_PREFS = ["No Preference", "Female", "Male"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function MultiCheck({ options, value, onChange, testid }) {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
        if (selected.includes(opt)) onChange(selected.filter((s) => s !== opt));
        else onChange([...selected, opt]);
    };
    return (
        <div data-testid={testid} className="flex flex-wrap gap-2">
            {options.map((opt) => {
                const on = selected.includes(opt);
                return (
                    <button
                        key={opt}
                        type="button"
                        onClick={() => toggle(opt)}
                        data-testid={`${testid}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`h-9 px-3.5 rounded-full text-xs font-semibold border transition ${
                            on
                                ? "bg-aduku-navy text-white border-aduku-navy"
                                : "bg-white text-aduku-navy border-aduku-border hover:border-aduku-navy"
                        }`}
                    >
                        {opt}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Category-specific fields for the 4 new service categories. All values
 * persist on `listing.meta` (flat key/value object), exactly like JobsMetaForm.
 *
 * Props:
 *   category — current category slug
 *   meta     — current meta object
 *   onChange — fn(nextMeta)
 */
export default function ServicesMetaForm({ category, meta = {}, onChange }) {
    const set = (key, value) => onChange({ ...meta, [key]: value });

    if (category === "event-management") {
        return (
            <section data-testid="event-mgmt-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Event service details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Help customers understand what you organise.</p>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Event types you handle</label>
                    <MultiCheck
                        testid="meta-event-types"
                        options={EVENT_TYPES}
                        value={meta.event_types}
                        onChange={(v) => set("event_types", v)}
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Capacity (max guests)</label>
                        <Input
                            data-testid="meta-capacity"
                            inputMode="numeric"
                            value={meta.capacity ?? ""}
                            onChange={(e) => set("capacity", e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="e.g. 500"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Price unit</label>
                        <Select value={meta.price_unit || ""} onValueChange={(v) => set("price_unit", v)}>
                            <SelectTrigger data-testid="meta-price-unit" className="mt-1"><SelectValue placeholder="Per Event / Per Day…" /></SelectTrigger>
                            <SelectContent>{PRICE_UNITS_EVENT.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Packages / inclusions</label>
                    <Textarea
                        data-testid="meta-packages"
                        rows={2}
                        value={meta.packages || ""}
                        onChange={(e) => set("packages", e.target.value)}
                        maxLength={500}
                        placeholder="e.g. Decoration, catering, sound, photography, anchor…"
                        className="mt-1"
                    />
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Availability days</label>
                    <MultiCheck
                        testid="meta-availability"
                        options={WEEKDAYS}
                        value={meta.availability_days}
                        onChange={(v) => set("availability_days", v)}
                    />
                </div>

                <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-aduku-navy">On-site service</div>
                        <div className="text-xs text-aduku-navy/55">Toggle off if you only do venue-based events.</div>
                    </div>
                    <Switch
                        data-testid="meta-onsite"
                        checked={!!meta.on_site}
                        onCheckedChange={(v) => set("on_site", v)}
                    />
                </div>
            </section>
        );
    }

    if (category === "groceries") {
        return (
            <section data-testid="groceries-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Grocery store details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Buyers want to know your range, delivery and payment options.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Store type</label>
                        <Select value={meta.shop_type || ""} onValueChange={(v) => set("shop_type", v)}>
                            <SelectTrigger data-testid="meta-shop-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Kirana / General", "Supermarket", "Wholesale", "Organic Store", "Online-only"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Min order value (₹)</label>
                        <Input
                            data-testid="meta-min-order"
                            inputMode="numeric"
                            value={meta.min_order_value ?? ""}
                            onChange={(e) => set("min_order_value", e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="e.g. 200"
                            className="mt-1"
                        />
                    </div>
                </div>

                <div className="bg-white border border-aduku-border rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-aduku-navy">Home delivery available</div>
                            <div className="text-xs text-aduku-navy/55">Show buyers you deliver near them.</div>
                        </div>
                        <Switch
                            data-testid="meta-delivery"
                            checked={!!meta.delivery_available}
                            onCheckedChange={(v) => set("delivery_available", v)}
                        />
                    </div>
                    {meta.delivery_available && (
                        <div>
                            <label className="text-xs font-semibold text-aduku-navy/70">Delivery radius (km)</label>
                            <Input
                                data-testid="meta-delivery-radius"
                                inputMode="numeric"
                                value={meta.delivery_radius_km ?? ""}
                                onChange={(e) => set("delivery_radius_km", e.target.value.replace(/\D/g, "").slice(0, 3))}
                                placeholder="e.g. 5"
                                className="mt-1"
                            />
                        </div>
                    )}
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Payment modes accepted</label>
                    <MultiCheck
                        testid="meta-payment-modes"
                        options={PAY_MODES}
                        value={meta.payment_modes}
                        onChange={(v) => set("payment_modes", v)}
                    />
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Operating hours</label>
                    <Input
                        data-testid="meta-hours"
                        value={meta.operating_hours || ""}
                        onChange={(e) => set("operating_hours", e.target.value)}
                        placeholder="e.g. 7:00 AM – 10:00 PM"
                        maxLength={60}
                        className="mt-1"
                    />
                </div>
            </section>
        );
    }

    if (category === "fruits-vegetables") {
        return (
            <section data-testid="fruits-veg-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Produce details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Tell buyers what you grow / sell.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Produce type</label>
                        <Select value={meta.produce_type || ""} onValueChange={(v) => set("produce_type", v)}>
                            <SelectTrigger data-testid="meta-produce-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Fruits", "Vegetables", "Fruits & Vegetables", "Leafy Greens", "Exotic / Imported"].map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Sale unit</label>
                        <Select value={meta.sale_unit || ""} onValueChange={(v) => set("sale_unit", v)}>
                            <SelectTrigger data-testid="meta-sale-unit" className="mt-1"><SelectValue placeholder="Per Kg / Per Dozen…" /></SelectTrigger>
                            <SelectContent>{PRODUCE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Items available</label>
                    <Textarea
                        data-testid="meta-items"
                        rows={2}
                        value={meta.items || ""}
                        onChange={(e) => set("items", e.target.value)}
                        maxLength={500}
                        placeholder="e.g. Apple, Banana, Mango, Tomato, Onion…"
                        className="mt-1"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Trade type</label>
                        <Select value={meta.trade_type || ""} onValueChange={(v) => set("trade_type", v)}>
                            <SelectTrigger data-testid="meta-trade-type" className="mt-1"><SelectValue placeholder="Wholesale / Retail" /></SelectTrigger>
                            <SelectContent>
                                {["Retail", "Wholesale", "Both"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Min bulk quantity (kg)</label>
                        <Input
                            data-testid="meta-min-bulk"
                            inputMode="numeric"
                            value={meta.min_bulk_qty ?? ""}
                            onChange={(e) => set("min_bulk_qty", e.target.value.replace(/\D/g, "").slice(0, 5))}
                            placeholder="e.g. 25"
                            className="mt-1"
                        />
                    </div>
                </div>

                <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-aduku-navy">Organic / pesticide-free</div>
                        <div className="text-xs text-aduku-navy/55">Highlight if your produce is certified or chemical-free.</div>
                    </div>
                    <Switch
                        data-testid="meta-organic"
                        checked={!!meta.organic}
                        onCheckedChange={(v) => set("organic", v)}
                    />
                </div>
            </section>
        );
    }

    if (category === "home-maid") {
        return (
            <section data-testid="home-maid-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Home help / maid details</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Households want to find the right helper at the right time.</p>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Service type</label>
                    <Select value={meta.service_type || ""} onValueChange={(v) => set("service_type", v)}>
                        <SelectTrigger data-testid="meta-service-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{HOMEMAID_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Rate unit</label>
                        <Select value={meta.rate_unit || ""} onValueChange={(v) => set("rate_unit", v)}>
                            <SelectTrigger data-testid="meta-rate-unit" className="mt-1"><SelectValue placeholder="Per Hour / Per Month" /></SelectTrigger>
                            <SelectContent>{HOMEMAID_RATE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Experience (yrs)</label>
                        <Input
                            data-testid="meta-experience"
                            inputMode="numeric"
                            value={meta.experience_years ?? ""}
                            onChange={(e) => set("experience_years", e.target.value.replace(/\D/g, "").slice(0, 2))}
                            placeholder="e.g. 3"
                            className="mt-1"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Languages</label>
                        <Input
                            data-testid="meta-languages"
                            value={meta.languages || ""}
                            onChange={(e) => set("languages", e.target.value)}
                            placeholder="e.g. Hindi, Kannada, English"
                            maxLength={120}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Gender preference</label>
                        <Select value={meta.gender_preference || ""} onValueChange={(v) => set("gender_preference", v)}>
                            <SelectTrigger data-testid="meta-gender-pref" className="mt-1"><SelectValue placeholder="No Preference" /></SelectTrigger>
                            <SelectContent>{GENDER_PREFS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Available days</label>
                    <MultiCheck
                        testid="meta-available-days"
                        options={WEEKDAYS}
                        value={meta.available_days}
                        onChange={(v) => set("available_days", v)}
                    />
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Working hours</label>
                    <Input
                        data-testid="meta-working-hours"
                        value={meta.working_hours || ""}
                        onChange={(e) => set("working_hours", e.target.value)}
                        placeholder="e.g. 6 AM – 10 AM, 6 PM – 9 PM"
                        maxLength={80}
                        className="mt-1"
                    />
                </div>

                <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-aduku-navy">Live-in available</div>
                        <div className="text-xs text-aduku-navy/55">Toggle on if you accept full-time live-in placements.</div>
                    </div>
                    <Switch
                        data-testid="meta-live-in"
                        checked={!!meta.live_in}
                        onCheckedChange={(v) => set("live_in", v)}
                    />
                </div>
            </section>
        );
    }

    if (category === "mutual-fund-distributor") {
        return (
            <section data-testid="mf-distributor-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Mutual Fund Distributor profile</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Investors look for licensed, transparent distributors. Fill in your registration & products you advise on.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">ARN number *</label>
                        <Input
                            data-testid="meta-arn"
                            value={meta.arn || ""}
                            onChange={(e) => set("arn", e.target.value.toUpperCase().slice(0, 20))}
                            placeholder="e.g. ARN-123456"
                            className="mt-1 font-mono tracking-wider"
                        />
                        <div className="text-[11px] text-aduku-navy/50 mt-1">AMFI Registration Number — mandatory in India.</div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">EUIN <span className="text-aduku-navy/50 font-normal">(optional)</span></label>
                        <Input
                            data-testid="meta-euin"
                            value={meta.euin || ""}
                            onChange={(e) => set("euin", e.target.value.toUpperCase().slice(0, 20))}
                            placeholder="e.g. E123456"
                            className="mt-1 font-mono tracking-wider"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Product types you advise on</label>
                    <MultiCheck
                        testid="meta-mf-products"
                        options={["Equity SIP", "Equity Lumpsum", "ELSS (Tax-saving)", "Debt Funds", "Hybrid Funds", "Index Funds", "Liquid / Overnight", "International Funds", "ETFs", "NPS"]}
                        value={meta.product_types}
                        onChange={(v) => set("product_types", v)}
                    />
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">AMC partners <span className="text-aduku-navy/50 font-normal">(comma-separated)</span></label>
                    <Input
                        data-testid="meta-mf-amcs"
                        value={meta.amc_partners || ""}
                        onChange={(e) => set("amc_partners", e.target.value)}
                        placeholder="e.g. HDFC AMC, SBI MF, ICICI Prudential, Axis MF"
                        maxLength={300}
                        className="mt-1"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Advisory mode</label>
                        <Select value={meta.advisory_mode || ""} onValueChange={(v) => set("advisory_mode", v)}>
                            <SelectTrigger data-testid="meta-mf-advisory-mode" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Online (Video/Chat)", "In-person", "Both"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Experience (yrs)</label>
                        <Input
                            data-testid="meta-mf-experience"
                            inputMode="numeric"
                            value={meta.experience_years ?? ""}
                            onChange={(e) => set("experience_years", e.target.value.replace(/\D/g, "").slice(0, 2))}
                            placeholder="e.g. 5"
                            className="mt-1"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Languages you advise in</label>
                    <Input
                        data-testid="meta-mf-languages"
                        value={meta.languages || ""}
                        onChange={(e) => set("languages", e.target.value)}
                        placeholder="e.g. English, Hindi, Kannada"
                        maxLength={120}
                        className="mt-1"
                    />
                </div>

                <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-aduku-navy">Complimentary first consultation</div>
                        <div className="text-xs text-aduku-navy/55">Toggle on to attract more enquiries.</div>
                    </div>
                    <Switch
                        data-testid="meta-mf-free-consult"
                        checked={!!meta.free_consultation}
                        onCheckedChange={(v) => set("free_consultation", v)}
                    />
                </div>

                <div className="text-[11px] text-aduku-navy/55 leading-relaxed bg-white border border-aduku-border rounded-lg p-3">
                    <b>Compliance reminder:</b> Mutual fund investments are subject to market risks. Please ensure your listing complies with AMFI / SEBI advertising guidelines.
                </div>
            </section>
        );
    }

    if (category === "insurance-distributor") {
        return (
            <section data-testid="insurance-distributor-meta" className="bg-aduku-yellow/10 border border-aduku-yellow/40 rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="font-display font-bold text-xl text-aduku-navy">Insurance Distributor profile</h2>
                    <p className="text-sm text-aduku-navy/65 mt-0.5">Customers want a licensed advisor across the policy types they need.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">IRDAI / intermediary licence *</label>
                        <Input
                            data-testid="meta-irdai"
                            value={meta.irdai_license || ""}
                            onChange={(e) => set("irdai_license", e.target.value.toUpperCase().slice(0, 30))}
                            placeholder="e.g. IRDAI/CA/0123 or POSP code"
                            className="mt-1 font-mono tracking-wider"
                        />
                        <div className="text-[11px] text-aduku-navy/50 mt-1">Agent / broker / POSP license number — mandatory in India.</div>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Distributor type</label>
                        <Select value={meta.distributor_type || ""} onValueChange={(v) => set("distributor_type", v)}>
                            <SelectTrigger data-testid="meta-ins-distributor-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Individual Agent", "POSP (Point-of-Sale)", "Corporate Agent", "Insurance Broker", "Web Aggregator"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Policy types you sell</label>
                    <MultiCheck
                        testid="meta-ins-policies"
                        options={["Term Life", "Whole Life", "ULIP", "Endowment", "Health (Individual)", "Family Floater", "Senior Citizen Health", "Critical Illness", "Motor (Car)", "Motor (Two-wheeler)", "Travel", "Home", "Business / Liability", "Cyber"]}
                        value={meta.policy_types}
                        onChange={(v) => set("policy_types", v)}
                    />
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Insurer partners <span className="text-aduku-navy/50 font-normal">(comma-separated)</span></label>
                    <Input
                        data-testid="meta-ins-partners"
                        value={meta.insurer_partners || ""}
                        onChange={(e) => set("insurer_partners", e.target.value)}
                        placeholder="e.g. LIC, HDFC Life, Star Health, ICICI Lombard, Bajaj Allianz"
                        maxLength={300}
                        className="mt-1"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Advisory mode</label>
                        <Select value={meta.advisory_mode || ""} onValueChange={(v) => set("advisory_mode", v)}>
                            <SelectTrigger data-testid="meta-ins-advisory-mode" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                {["Online (Video/Chat)", "In-person", "Both"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-aduku-navy">Experience (yrs)</label>
                        <Input
                            data-testid="meta-ins-experience"
                            inputMode="numeric"
                            value={meta.experience_years ?? ""}
                            onChange={(e) => set("experience_years", e.target.value.replace(/\D/g, "").slice(0, 2))}
                            placeholder="e.g. 7"
                            className="mt-1"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold text-aduku-navy">Languages you advise in</label>
                    <Input
                        data-testid="meta-ins-languages"
                        value={meta.languages || ""}
                        onChange={(e) => set("languages", e.target.value)}
                        placeholder="e.g. English, Hindi, Tamil"
                        maxLength={120}
                        className="mt-1"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-aduku-navy">Complimentary claim assistance</div>
                            <div className="text-xs text-aduku-navy/55">Help with claim filing & follow-up.</div>
                        </div>
                        <Switch
                            data-testid="meta-ins-claim-help"
                            checked={!!meta.claim_assistance}
                            onCheckedChange={(v) => set("claim_assistance", v)}
                        />
                    </div>
                    <div className="bg-white border border-aduku-border rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-aduku-navy">Complimentary first consultation</div>
                            <div className="text-xs text-aduku-navy/55">Attract more enquiries.</div>
                        </div>
                        <Switch
                            data-testid="meta-ins-free-consult"
                            checked={!!meta.free_consultation}
                            onCheckedChange={(v) => set("free_consultation", v)}
                        />
                    </div>
                </div>

                <div className="text-[11px] text-aduku-navy/55 leading-relaxed bg-white border border-aduku-border rounded-lg p-3">
                    <b>Compliance reminder:</b> Insurance is a subject matter of solicitation. Listings must comply with IRDAI advertising regulations.
                </div>
            </section>
        );
    }

    return null;
}
